import { stateful, useObservable } from '@adornis/functional-lit/stateful.js';
import { goTo } from '@adornis/router/client/open-href.js';
import { currentUser } from '@adornis/users/client/currentUser.js';
import { ActionGrid, type DashboardAction } from '@martinus/action-grid/client/ActionGrid.js';
import { PageHeader } from '@martinus/styles/client/PageHeader.js';
import { html, nothing } from 'lit';
import { hasRoles } from '../../permissions/api/hasRoles.js';
import { Permission, Role } from '../../permissions/roles.js';
import { openCreateUserDialog } from '../../users/client/CreateUserDialog.js';
import type { Person } from '../../users/entities/person.js';
import { showAbwesenheitBeantragenDialog } from '../components/dialogs/sg-abwesenheit-beantragen-dialog.js';

const actions: DashboardAction[] = [
  {
    name: 'SuS Bewerbungsformular',
    icon: 'icon-[material-symbols--escalator-warning-rounded]',
    color: 'bg-success text-success-content',
    show: () => true,
    action: () => {
      goTo('/sus-schulen');
    },
  },
  {
    name: 'Kandidat/Schüler anlegen',
    icon: 'icon-[material-symbols--person-add-rounded]',
    color: 'bg-success text-success-content',
    show: user => {
      if (user?.hasPermission(Permission.CREATE_USER)) return true;
      return false;
    },
    action: async () => {
      await openCreateUserDialog();
    },
  },
  {
    name: 'Quittungen herunterladen',
    icon: 'icon-[material-symbols--receipt-long-rounded]',
    color: 'bg-secondary text-secondary-content',
    show: user => {
      if (user?.roles.find(r => r.name === Role.SPENDER) || user?.roles.find(r => r.name === Role.SORGEBERECHTIGTER))
        return true;
      return false;
    },
    action: () => {
      goTo('/quittungen');
    },
  },
  {
    name: 'Lastschriftmandate',
    icon: 'icon-[material-symbols--payments-rounded]',
    color: 'bg-accent text-accent-content',
    show: user => {
      if (user?.roles.find(r => r.name === Role.SPENDER) || user?.roles.find(r => r.name === Role.SORGEBERECHTIGTER))
        return true;
      return false;
    },
    action: (user: Person) => {
      goTo(`personen/${user._id}/sepa`);
    },
  },
  {
    name: 'Abwesenheiten eintragen',
    icon: 'icon-[material-symbols--event-busy-rounded]',
    color: 'bg-warning text-warning-content',
    show: user => {
      if (user?.roles.find(r => r.name === Role.MITARBEITER)) return true;
      return false;
    },
    action: async user => {
      if (!user) return;
      const newAbwesenheitID = await showAbwesenheitBeantragenDialog({
        personID: user._id,
        ganztaegig: true,
        modal: true,
      });
      if (newAbwesenheitID) goTo('/abwesenheiten');
    },
  },
  {
    name: 'Eintrittsformular',
    icon: 'icon-[material-symbols--contact-page-rounded]',
    color: 'bg-primary text-primary-content',
    show: user => {
      if (user && hasRoles({ user, roles: [Role.KANDIDAT, Role.MITARBEITER, Role.OLD_EMPLOYEE] })) return true;
      return false;
    },
    action: () => {
      goTo('/eintrittsformular');
    },
  },
  {
    name: 'Microsoft-Login',
    icon: 'icon-[material-symbols--login-rounded]',
    color: 'bg-info text-info-content',
    show: user => {
      if (user?.roles.find(r => r.name === Role.MITARBEITER) || user?.roles.find(r => r.name === Role.ADMIN))
        return true;
      return false;
    },
    action: () => {
      goTo(`https://myaccount.microsoft.com/`, { target: '_blank' });
    },
  },
];

export const MainDashboard = stateful(() => {
  const user = useObservable(() => currentUser<Person>(), []);

  if (!user) return nothing;

  return html`
    ${PageHeader({
      title: 'Dashboard',
      rightContent: html`
        <div class="flex flex-col items-end">
          <span class="font-bold">
            ${`${user.profilDaten?.stammdaten?.vorname ?? user.email} ${
              user.profilDaten?.stammdaten?.nachname ?? user.debitorennummer
            }`}
          </span>
          <span class="text-sm text-base-content/70">${user.roles.map(r => r.name).join(', ')}</span>
        </div>
      `,
    })}
    <div class="card-container">${ActionGrid({ actions, onEmpty: () => goTo(`/personen/${user._id}`) })}</div>
  `;
});
